import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SidebarLinkGroup } from "./SidebarLinkGroup";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { MdConstruction, MdOutlineWidgets } from "react-icons/md";
import { GoGoal } from "react-icons/go";
import { SocketContext } from "./Context/SocketContext";
import { AnyGoalDoc, NewGoalDeleteEvent, NewGoalUpdateEvent, SubathonDocument, UpdateSubathonEvent } from "@ailenax-overlay/shared";
import { FaCogs, FaHome, FaRegGem } from "react-icons/fa";
import { GiFinishLine } from "react-icons/gi";
import { GrSteps } from "react-icons/gr";
import { RiGeminiLine, RiGift2Fill } from "react-icons/ri";
import { getCurrentTitle } from "./lib/goals";
import { donationIcons } from "./lib/utils";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useAuth } from "./hooks/useAuth";

interface SidebarProps {
    variant: "default" | "v2";
}

const goalTypeOrder: Record<AnyGoalDoc["goalType"], number> = {
    simple: 0,
    multistep: 1,
};

const kindOrder: Record<AnyGoalDoc["kind"], number> = {
    bits: 0,
    subs: 1,
    donation: 2,
};

export const Sidebar: FC<SidebarProps> = ({ variant }) => {
    const location = useLocation();
    const { pathname } = location;
    const socket = useContext(SocketContext);
    const [newGoals, setNewGoals] = useState<AnyGoalDoc[]>([]);
    const [subathons, setSubathons] = useState<SubathonDocument[]>([]);

    const [, setRemember] = useLocalStorage("remember-token", false);
    const [, setRemembered] = useLocalStorage("remembered-token", "");
    const { setToken } = useAuth();
    const navigate = useNavigate();

    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        socket.emit("get-subathons", s => setSubathons(s));

        socket.emit("get-new-goals", g => setNewGoals(g));

        const onNewGoalUpdate = (e: NewGoalUpdateEvent) => {
            setNewGoals(prev => {
                const index = prev.findIndex(g => g._id === e.goal._id);
                if (index === -1) {
                    return [...prev, e.goal];
                }

                prev[index] = e.goal;
                return [...prev];
            });
        }

        socket.on("new-goal-update", onNewGoalUpdate);

        const onNewGoalDelete = (e: NewGoalDeleteEvent) => {
            setNewGoals(prev => prev.filter(g => String(g._id) !== e.goalId));
        };

        socket.on("new-goal-delete", onNewGoalDelete);

        const onSubathonUpdate = (e: UpdateSubathonEvent) => {
            setSubathons(prev => {
                const index = prev.findIndex(s => s._id === e.subathon._id);
                if (index === -1) {
                    return [...prev, e.subathon];
                }

                prev[index] = e.subathon;
                return [...prev];
            });
        }
        
        socket.on("update-subathon", onSubathonUpdate);

        return () => {
            socket.off("new-goal-update", onNewGoalUpdate);
            socket.off("new-goal-delete", onNewGoalDelete);
            socket.off("update-subathon", onSubathonUpdate);
        }
    }, [socket]);

    const sortedGoals = useMemo(() => newGoals.toSorted((a: AnyGoalDoc, b: AnyGoalDoc) => {
        const goalCompare = goalTypeOrder[a.goalType] - goalTypeOrder[b.goalType];
        const kindCompare = kindOrder[a.kind] - kindOrder[b.kind];
        if (goalCompare !== 0) return goalCompare;
        if (kindCompare !== 0) return kindCompare;

        return (a._id as string).localeCompare(b._id as string);
    }), [newGoals]);

    const handleLogout = useCallback(() => {
        setRemember(false);
        setRemembered("");
        setToken("");
        navigate("/login");
    }, [setRemember, setRemembered, setToken, navigate]);

    return (
        <aside className="fixed min-w-fit font-inter">
            <div
                id="sidebar"
                className={`flex lg:flex! flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:translate-x-0 h-[100dvh] overflow-y-scroll lg:overlfow-y-auto no-scrollbar w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:w-64! shrink-0 bg-white dark:bg-gray-800 p-4 transition-all duration-200 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-64"} ${variant === 'v2' ? 'border-r border-gray-200 dark:border-gray-700/60' : 'rounded-r-2xl shadow-xs'}`}
            >
                <div className="flex justify-between mb-10 pr-3 sm:px-2">
                    <button
                        className="text-gray-500 hover:text-gray-400"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-8">
                    <div>
                        <h3 className="text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold pl-3">
                            <span className="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-6" aria-hidden="true">
                                •••
                            </span>
                            <span className="lg:hidden lg:sidebar-expanded:block 2xl:block">Pages</span>
                        </h3>
                        <ul className="mt-3">
                            {/* Home */}
                            <SidebarLinkGroup activeCondition={pathname === "/home" || pathname === "/session"}>
                                {(handleClick, open) => {
                                    return (
                                        <>
                                            <a
                                                href="#0"
                                                className={`block text-gray-800 dark:text-gray-100 truncate transition duration-150 ${(pathname === "/home" || pathname === "/session") ? "" : "hover:text-gray-900 dark:hover:text-white"}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClick();
                                                    setSidebarOpen(true);
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <FaHome />
                                                        <span className="text-sm font-medium ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Home
                                                        </span>
                                                    </div>
                                                    <div className="flex shrink-0 ml-2">
                                                        <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500 ${open && "rotate-180"}`} viewBox="0 0 12 12">
                                                            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </a>

                                            <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                                                <ul className={`pl-8 mt-1 ${!open && "hidden"}`}>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to="/home"
                                                            className={({ isActive }) => "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")}
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Dashboard
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to="/session"
                                                            className={({ isActive }) => "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")}
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Session
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                </ul>
                                            </div>
                                        </>
                                    )
                                }}
                            </SidebarLinkGroup>


                            {/* Widgets */}
                            <SidebarLinkGroup activeCondition={pathname.startsWith("/widgets")}>
                                {(handleClick, open) => {
                                    return (
                                        <>
                                            <a
                                                href="#0"
                                                className={`block text-gray-800 dark:text-gray-100 truncate transition duration-150 ${pathname.includes("widgets") ? "" : "hover:text-gray-900 dark:hover:text-white"}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClick();
                                                    setSidebarOpen(true);
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <MdOutlineWidgets />
                                                        <span className="text-sm font-medium ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Widgets
                                                        </span>
                                                    </div>
                                                    <div className="flex shrink-0 ml-2">
                                                        <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500 ${open && "rotate-180"}`} viewBox="0 0 12 12">
                                                            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </a>

                                            <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                                                <ul className={`pl-8 mt-1 ${!open && "hidden"}`}>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to="/widgets/donotrain"
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                Donation-Train
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                </ul>
                                            </div>
                                        </>
                                    )
                                }}
                            </SidebarLinkGroup>

                            {/* Goals */}
                            <SidebarLinkGroup activeCondition={pathname.startsWith("/goals")}>
                                {(handleClick, open) => {
                                    return (
                                        <>
                                            <a
                                                href="#0"
                                                className={`block text-gray-800 dark:text-gray-100 truncate transition duration-150 ${pathname.includes("goals") ? "" : "hover:text-gray-900 dark:hover:text-white"}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClick();
                                                    setSidebarOpen(true);
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <GoGoal />
                                                        <span className="text-sm font-medium ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Goals
                                                        </span>
                                                    </div>
                                                    <div className="flex shrink-0 ml-2">
                                                        <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500 ${open && "rotate-180"}`} viewBox="0 0 12 12">
                                                            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </a>
                                            <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                                                <ul className={`pl-8 mt-1 ${!open && "hidden"}`}>
                                                    { sortedGoals.map(goal => (
                                                        <li key={`goal-${goal._id}`} className="mb-1 last:mb-0">
                                                            <NavLink
                                                                end
                                                                to={`/goals/${goal._id}`}
                                                                className={({ isActive }) =>
                                                                    "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                                }
                                                            >
                                                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                    { goal.goalType === "multistep" && <GrSteps className="inline-block mr-2" />}
                                                                    { goal.goalType === "simple" && <RiGeminiLine className="inline-block mr-2" />}
                                                                    { goal.kind === "donation" && donationIcons[import.meta.env.VITE_DONATION_CURRENCY] }
                                                                    { goal.kind === "bits" && <FaRegGem className="inline-block mr-2" /> }
                                                                    { goal.kind === "subs" && <RiGift2Fill className="inline-block mr-2" /> }
                                                                    {getCurrentTitle(goal)}
                                                                </span>
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                        <li className="mt-5 mb-1 last:mb-0">
                                                            <NavLink
                                                                end
                                                                to="/goals/new"
                                                                className={({ isActive }) =>
                                                                    "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                                }
                                                            >
                                                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                    + New Goal
                                                                </span>
                                                            </NavLink>
                                                        </li>
                                                </ul>
                                            </div>
                                        </>
                                    )
                                }}
                            </SidebarLinkGroup>

                            {/* Subathons */}
                            { import.meta.env.VITE_FEATURE_SUBATHON !== false && (<SidebarLinkGroup activeCondition={pathname.startsWith("/subathons")}>
                                {(handleClick, open) => {
                                    return (
                                        <>
                                            <a
                                                href="#0"
                                                className={`block text-gray-800 dark:text-gray-100 truncate transition duration-150 ${pathname.includes("goals") ? "" : "hover:text-gray-900 dark:hover:text-white"}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClick();
                                                    setSidebarOpen(true);
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <GiFinishLine />
                                                        <span className="text-sm font-medium ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Subathons
                                                        </span>
                                                    </div>
                                                    <div className="flex shrink-0 ml-2">
                                                        <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500 ${open && "rotate-180"}`} viewBox="0 0 12 12">
                                                            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </a>
                                            <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                                                <ul className={`pl-8 mt-1 ${!open && "hidden"}`}>
                                                    { subathons.map(subathon => (
                                                        <li key={`goal-${subathon._id}`} className="mb-1 last:mb-0">
                                                            <NavLink
                                                                end
                                                                to={`/subathons/${subathon._id}`}
                                                                className={({ isActive }) =>
                                                                    "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                                }
                                                            >
                                                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                    {subathon.title}
                                                                </span>
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                        <li className="mt-5 mb-1 last:mb-0">
                                                            <NavLink
                                                                end
                                                                to={`/home`}
                                                                className={({ isActive }) =>
                                                                    "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                                }
                                                            >
                                                                <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                    <MdConstruction style={{ display: "inline-block" }} color="#f7cd4c" title="Under Construction"/> + New Subathon
                                                                </span>
                                                            </NavLink>
                                                        </li>
                                                </ul>
                                            </div>
                                        </>
                                    )
                                }}
                            </SidebarLinkGroup>)}

                            {/* Settings */}
                            <SidebarLinkGroup activeCondition={pathname.startsWith("/settings")}>
                                {(handleClick, open) => {
                                    return (
                                        <>
                                            <a
                                                href="#0"
                                                className={`block text-gray-800 dark:text-gray-100 truncate transition duration-150 ${pathname.includes("settings") ? "" : "hover:text-gray-900 dark:hover:text-white"}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClick();
                                                    setSidebarOpen(true);
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <FaCogs />
                                                        <span className="text-sm font-medium ml-4 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                            Settings
                                                        </span>
                                                    </div>
                                                    <div className="flex shrink-0 ml-2">
                                                        <svg className={`w-3 h-3 shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500 ${open && "rotate-180"}`} viewBox="0 0 12 12">
                                                            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </a>
                                            <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                                                <ul className={`pl-8 mt-1 ${!open && "hidden"}`}>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to={`/settings/twitch`}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                Twitch
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to={`/settings/kofi`}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                Ko-fi
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to={`/settings/streamelements`}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                StreamElements
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to={`/settings/tipeee`}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                Tipeee
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to={`/home`}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                <MdConstruction style={{ display: "inline-block" }} color="#f7cd4c" title="Under Construction"/> Chat Fallback
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 mt-5 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to={`/settings/chatbot`}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                Chat-Bot
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                    <li className="mb-1 mt-5 last:mb-0">
                                                        <NavLink
                                                            end
                                                            to="/login"
                                                            onClick={handleLogout}
                                                            className={({ isActive }) =>
                                                                "block transition duration-150 truncate " + (isActive ? "text-violet-500" : "text-gray-500/90 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
                                                            }
                                                        >
                                                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                                                Logout
                                                            </span>
                                                        </NavLink>
                                                    </li>
                                                </ul>
                                            </div>
                                        </>
                                    )
                                }}
                            </SidebarLinkGroup>

                            <li className="absolute bottom-5">
                                <MdConstruction style={{ display: "inline-block" }} color="#f7cd4c" title="Under Construction"/> = Under Construction
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </aside>
    )
}